import React, { useEffect, useState } from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";
import { useNavigation, useRoute } from "@react-navigation/native";
import type { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { MotiView } from "moti";
import { ArrowLeft } from "@phosphor-icons/react";

import DefaultLayout from "../../layouts/DefaultLayout.web";
import AdminTable from "../../components/adminDashboard/AdminTable/AdminTable";
import AdminTableHeader from "../../components/adminDashboard/AdminTable/AdminTableHeader";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import { useGetVisitSessionsQuery } from "../../graphql/generated/graphql";
import type { RootStackParamList } from "../../navigation/AppNavigator.web";
import useSortingDropdown from "../../hooks/useSortingDropdown";
import usePagination from "../../hooks/usePagination";
import AdminTablePagination from "../../components/adminDashboard/AdminTable/AdminTablePagination";
import UserSessionTable from "../../components/adminDashboard/Analytics/UserSessionTable";

const SORT_ITEMS = [
  { label: "Date (Newest First)", value: "date_desc" },
  { label: "Date (Oldest First)", value: "date_asc" },
];

interface RouteParams {
  userId: string;
}

const UserSessionDetailsView: React.FC = () => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute();
  const { userId } = route.params as RouteParams;

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const { sortBy, setSortBy, sortDropdownOpen, setSortDropdownOpen } = useSortingDropdown({
    defaultSortBy: "date_desc",
  });

  const { page, setPage, pageSize, setPageSize } = usePagination({ itemsPerPage: 10 });

  const {
    data: sessionsData,
    loading,
    refetch: sessionsRefetch,
  } = useGetVisitSessionsQuery({
    variables: {
      input: {
        userId,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        page,
        pageSize,
        sortBy,
      },
    },
    skip: !userId,
  });

  const sessions = sessionsData?.getVisitSessions?.data?.visitSessions || [];
  const totalCount = sessionsData?.getVisitSessions?.data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);
  const userName = sessionsData?.getVisitSessions?.data?.visitSessions[0]?.userName || "";

  useEffect(() => {
    setPage(1);
  }, [sortBy, startDate, endDate]);

  useEffect(() => {
    sessionsRefetch();
  }, [page, pageSize, sortBy, startDate, endDate]);

  const handleBack = () => {
    navigation.navigate("Session Analytics");
  };

  return (
    <DefaultLayout pageHasChat={false}>
      <View style={styles.pageBackground}>
        <View style={styles.contentContainer}>
          <MotiView
            from={{ opacity: 0, translateY: 20, scale: 1 }}
            animate={{ opacity: 1, translateY: 0, scale: 1 }}
            delay={10}
            // @ts-expect-error - transition is not typed
            transition={{ type: "timing", duration: 500 }}>
            <CustomButton
              text="Back"
              styleType="transparent"
              onPress={handleBack}
              useTranslationText={false}
              style={styles.backButton}
              textStyle={styles.backButtonText}
              leftIcon={<ArrowLeft size={16} color="#64748B" />}
            />

            <CustomSpacing type="vertical" size="l" />

            <View style={styles.header}>
              <View style={styles.headerLeft}>
                <CustomText
                  text={`Session Analytics - ${userName || "N/A"}`}
                  size="m"
                  textType="display"
                  weight="ultraBold"
                  style={styles.title}
                  useTranslationText={false}
                />
                <CustomText
                  text={`View session details for ${userName || "N/A"}.`}
                  size="m"
                  style={styles.subtitle}
                  useTranslationText={false}
                />
              </View>
            </View>

            <CustomSpacing type="vertical" size="l" />

            <View style={styles.totalCountContainer}>
              <CustomText
                text={`Total Sessions (${totalCount})`}
                size="l"
                useTranslationText={false}
                weight="bold"
                style={styles.totalCountText}
              />
            </View>

            <CustomSpacing type="vertical" size="m" />

            <AdminTable>
              <AdminTableHeader
                sortBy={sortBy}
                setSortBy={setSortBy}
                sortDropdownOpen={sortDropdownOpen}
                setSortDropdownOpen={setSortDropdownOpen}
                sortItems={SORT_ITEMS}
              />
              {loading ? (
                <View style={styles.loaderContainer}>
                  <ActivityIndicator size="large" color="#0084FF" />
                </View>
              ) : sessions.length > 0 ? (
                <UserSessionTable sessions={sessions} />
              ) : (
                <View style={styles.emptyState}>
                  <CustomText text="No sessions found." />
                </View>
              )}
              {totalCount > 0 && (
                <AdminTablePagination
                  currentPage={page}
                  totalPages={totalPages}
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={setPage}
                  onItemsPerPageChange={(newPageSize) => {
                    setPageSize(newPageSize);
                    setPage(1);
                  }}
                />
              )}
            </AdminTable>
          </MotiView>
        </View>
      </View>
    </DefaultLayout>
  );
};

const styles = StyleSheet.create({
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  emptyState: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  pageBackground: {
    flex: 1,
    // @ts-expect-error - works on web
    minHeight: "100vh",
  },
  contentContainer: {
    flex: 1,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: {
    flex: 1,
  },
  title: {
    color: "#101828",
    marginBottom: 4,
  },
  subtitle: {
    color: "#6B7280",
  },
  totalCountContainer: {
    width: "100%",
    marginBottom: 16,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  totalCountText: {
    color: "#111827",
  },
  backButton: {
    alignSelf: "flex-start",
    paddingHorizontal: 0,
  },
  backButtonText: {
    color: "#64748B",
    fontSize: 16,
  },
});

export default UserSessionDetailsView;
